import React, { useCallback, useEffect, useMemo } from "react";

import Form, {
  CustomRule,
  EmailRule,
  GroupItem,
  RequiredRule,
  SimpleItem,
} from "devextreme-react/form";
import SelectBox, {
  Button as SelectButton,
  SelectBoxTypes,
} from "devextreme-react/select-box";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import "devextreme/ui/lookup";
import { run } from "effection";
import sum from "hash-sum";
import { isEqual } from "lodash";
import { useTranslation } from "react-i18next";
import { sleep } from "starfx";

import {
  contractorInEditMode,
  contractorInSpvMode,
  SPV_CONTRACTOR_MIRROR,
} from "@app/state/constants";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { schema } from "@app/statefx/schema";
import {
  selectCurrentEditingContrator,
  temporaryContractorDataLookup,
} from "@app/statefx/selectors/uiContractorEditing";
import {
  contactSBoxDataset,
  selectCity,
  selectCountries,
  selectCountrySubdivisions,
  selectCurrentClientCFF,
  selectCurrentContractor,
  selectSelectedContactID,
} from "@app/statefx/selectors/uiSpvMainContractor.sel";
import { setContractorsEditingProp } from "@app/statefx/xThunks/uiContractorsEditing";
import {
  fxExecuteAnafQuery,
  setCurrentMirrorContactSelection,
} from "@app/statefx/xThunks/uiSpvMain";
import "@app/style/dx.generic.catetory-chooser-compact.min.css";
import { TISOCountry } from "@app/types/insider";

import { HeaderComponent } from "./contractorHead";
import "./contractors.scss";

import type { FormRef, FormTypes } from "devextreme-react/form";
import type { TContractor } from "@app/types/insider";
import type { TContractorFormSource } from "@app/types/insider";

const copydata = "copydata";
const watchFields = [
  "ClientName",
  "Address",
  "__address__continuation",
  "County",
  "RegistrationNumber",
  "Iban",
  "BankName",
  "Attention",
  "Phone",
  "Fax",
  "City",
  "CountryCode",
  "CountyCode",
  "Email",
  "PostalCode",
  "AdditionalAddress",
  "BuyerReference",
];

export const ContractorForm = ({
  source,
  frmConRef,
  mainConRef,
  caller = contractorInSpvMode,
}: {
  source: TContractorFormSource;
  frmConRef: React.RefObject<FormRef>;
  mainConRef?: React.RefObject<FormRef>;
  caller?: typeof contractorInSpvMode | typeof contractorInEditMode;
}) => {
  const { t } = useTranslation();
  const fxDispatch = useFxDispatch();
  const guardianRef = React.useRef<TContractor>(null);
  const [dataSet, setDataSet] = React.useState({} as TContractor);
  const [buttons, setButtons] = React.useState({} as TContractor);
  const [resetButtonOptions, setResetButtonOptions] = React.useState({
    disabled: true,
  });
  const isNotMain = source !== SPV_CONTRACTOR_MIRROR.CTR_MAIN;
  const formdata =
    source === SPV_CONTRACTOR_MIRROR.CTR_MAIN
      ? caller === contractorInSpvMode
        ? useFxSelector(selectCurrentClientCFF)
        : useFxSelector(selectCurrentEditingContrator)
      : (useFxSelector((s) =>
          selectCurrentContractor(s, { source })
        ) as TContractor);

  const sumRef = React.useRef({
    CFF_MAIN: "",
    CFF_TMP: "",
    CFF_MIRR_ARCHIVE: "",
    CFF_MIRR_ANAF: "",
  });

  const sumdata = useMemo(() => sum(formdata), [formdata, source]);
  sumRef.current[source] = sumdata;

  const _coutryList =
    source === SPV_CONTRACTOR_MIRROR.CTR_MAIN
      ? useFxSelector(selectCountries)
      : [];
  const coutryList = useMemo(() => _coutryList, [source]);

  const _countrySubdivisions =
    source === SPV_CONTRACTOR_MIRROR.CTR_MAIN
      ? useFxSelector((s) =>
          selectCountrySubdivisions(s, {
            CountryCode: dataSet.CountryCode,
          })
        )
      : [];
  const countrySubdivisions = useMemo(
    () => _countrySubdivisions,
    [source, dataSet.CountryCode]
  );

  const _cityOrDistrict =
    source === SPV_CONTRACTOR_MIRROR.CTR_MAIN
      ? useFxSelector((s) => selectCity(s, { CountyCode: dataSet.CountyCode }))
      : [];
  const cityOrDistrict = useMemo(
    () => _cityOrDistrict,
    [source, dataSet.CountyCode]
  );

  const _cityDataset =
    source === SPV_CONTRACTOR_MIRROR.CTR_MAIN
      ? useFxSelector((s) => schema.isoNewCities.selectTableAsList(s))
      : [];
  const cityDataset = useMemo(() => _cityDataset, [source]);

  const cityDataSource = new DataSource({
    store: new CustomStore({
      load: () => {
        return SPV_CONTRACTOR_MIRROR.CTR_MAIN ? cityOrDistrict : [];
      },
      key: "ISOID",
      insert: (values) => {
        window.fx.dispatch(schema.update(schema.isoNewCities.add(values)));
        return values;
      },
    }),
  });

  const _contactList =
    source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ARCHIVE
      ? useFxSelector(contactSBoxDataset)
      : [];
  const contactList = useMemo(() => _contactList, [source]);

  const _currentContactSelected =
    source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ARCHIVE
      ? useFxSelector(selectSelectedContactID)
      : "";

  const currentContactSelected = useMemo(
    () => _currentContactSelected,
    [source, dataSet.ClientId]
  );

  const _lookupTempContractors =
    source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_TMP && caller === "ie"
      ? useFxSelector(temporaryContractorDataLookup)
      : [];
  const lookupTempContractors = useMemo(
    () => _lookupTempContractors,
    [source, dataSet.CffId, caller]
  );

  const editingContractorState = useFxSelector(
    schema.uiContractorsEditing.select
  );
  const searchCIF = useMemo(
    () => editingContractorState.searchCIF,
    [editingContractorState.searchCIF]
  );

  const onOptionChanged = useCallback(
    (e: FormTypes.OptionChangedEvent) => {
      if (source === SPV_CONTRACTOR_MIRROR.CTR_MAIN) {
        if (e.name === "onOptionChanged") {
          const newState = sumRef.current[source] === sum(dataSet);
          if (newState !== resetButtonOptions.disabled) {
            setResetButtonOptions({
              ...resetButtonOptions,
              disabled: sumRef.current[source] === sum(dataSet),
            });
          }
        }
      }
    },
    [source, dataSet, resetButtonOptions]
  );

  const dxClientLookupOptions = {
    height: 60,
    items: lookupTempContractors,
    readOnly: false,
    value: searchCIF,
    displayExpr: "text",
    valueExpr: "value",
    grouped: false,
    placeHoleder: "Cautare in datele temporare",
    searchEnabled: true,
    showDataBeforeSearch: false,
    showCancelButton: true,
    showClearButton: true,
    wrapItemText: true,
    onSelectionChanged: (e) => {
      const v = e?.selectedItem?.value;
      if (v === searchCIF) return;
      fxDispatch(setContractorsEditingProp({ searchCIF: v || "" }));
    },
  };

  // we will setup an ad-hoc table to store the new city
  // https://js.devexpress.com/React/Documentation/ApiReference/UI_Components/dxSelectBox/Configuration/#onCustomItemCreating
  const customCityItemCreating = (
    args: SelectBoxTypes.CustomItemCreatingEvent
  ) => {
    if (!args.text) {
      args.customItem = null;
      return;
    }
    const cityIds = cityDataset.map((item: TISOCountry) => item.ISOID);
    const incrementedId = Math.max.apply(null, cityIds) + 1;
    const countryName = coutryList.find(
      (item) => item.COD_NATIONAL === dataSet.CountryCode
    )?.DEN_NATIONAL;
    const newItem: TISOCountry = {
      COD_NATIONAL: dataSet.CountryCode,
      DEN_NATIONAL: countryName,
      COD_SUBDIV: dataSet.CountyCode,
      DEN_SUBDIV: args.text,
      ISOID: incrementedId,
    };
    args.customItem = cityDataSource
      .store()
      .insert(newItem)
      .then(() => cityDataSource.load())
      .then(() => newItem)
      .catch((error) => {
        console.log("error", error);
        //throw new Error('Error creating new city');
        return null;
      });
  };

  const outOfScopeOfTVA = useMemo(() => {
    if (source !== SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF) {
      return undefined;
    }
    //https://static.anaf.ro/static/10/Anaf/Informatii_R/Servicii_web/doc_WS_V8.txt
    // "scpTVA": " -- true -pentru platitor in scopuri de tva / false in cazul in care nu
    // e platitor in scopuri de TVA la data cautata  --",
    if (["true", true].includes(dataSet.__alertaNeplatitorTVA)) {
      return true;
    } else {
      if (["false", false].includes(dataSet.__alertaNeplatitorTVA)) {
        return false;
      }
      return undefined;
    }
  }, [sumdata]);

  const shouldCopyTaxCode = useCallback(
    (cffvalue: string, tmpvalue: string): boolean => {
      let result = false;
      if (tmpvalue && cffvalue !== tmpvalue) {
        result = true;
      }
      // if the temp value is different but has no ro and points to the same CIF, then don't copy
      // this is common for sent invoices.
      if (
        result &&
        (tmpvalue || "").replaceAll("RO", "") ===
          (cffvalue || "").replaceAll("RO", "")
      ) {
        if ((cffvalue || "").includes("RO")) {
          result = false;
        }
      }
      return result;
    },
    []
  );

  const shouldCopyAnyField = useCallback(
    (cffvalue: string, tmpvalue: string): boolean => {
      let result = false;
      if (tmpvalue && cffvalue !== tmpvalue) {
        result = true;
      }
      return result;
    },
    []
  );

  const runChecks = function* () {
    if (source === SPV_CONTRACTOR_MIRROR.CTR_MAIN) {
      return;
    }

    const tmpData = dataSet || {};
    const ffData = mainConRef.current?.instance().option("formData") || {};
    const newButtons = { ...buttons };
    watchFields.forEach((field) => {
      if (shouldCopyAnyField(ffData[field], tmpData[field])) {
        newButtons[field] = copydata;
      } else {
        newButtons[field] = "";
      }
    });
    if (shouldCopyTaxCode(ffData["TaxCode"], tmpData["TaxCode"])) {
      newButtons["TaxCode"] = copydata;
    } else {
      newButtons["TaxCode"] = "";
    }
    if (!isEqual(newButtons, buttons)) {
      setButtons(newButtons);
    }
    yield* sleep(1);
  };

  const getButtonDef = (FieldName: string) => {
    const btndef =
      source !== SPV_CONTRACTOR_MIRROR.CTR_MAIN &&
      buttons?.[FieldName] === copydata
        ? [
            {
              name: `copydata-${FieldName}`,
              location: "before",
              options: {
                className: "dx-button-copydata",
                elementAttr: { class: "dx-button-copydata" },
                stylingMode: "text",
                icon: "chevrondoubleleft",
                type: "default",
                disabled: false,
                onClick: () => {
                  const data = mainConRef.current
                    ?.instance()
                    .option("formData");
                  const newMainData = {
                    ...data,
                    [FieldName]: dataSet?.[FieldName],
                  };
                  mainConRef.current
                    ?.instance()
                    .option("formData", newMainData);
                  isNotMain && run(runChecks);
                },
              },
            },
          ]
        : [];
    return btndef;
  };

  const copyAll = () => {
    isNotMain && run(runChecks);
    // copy all the valuse who's keys are set True in buttons
    const initData = mainConRef.current?.instance().option("formData");
    Object.entries(buttons).forEach(([key, value]) => {
      if (value === copydata) {
        initData[key] = dataSet[key];
      }
    });
    mainConRef.current?.instance().option("formData", initData);
    isNotMain && run(runChecks);
  };
  const runRefresh = () => {
    if (source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_ANAF) {
      fxDispatch(fxExecuteAnafQuery(`${dataSet.TaxCode}--force`));
      return;
    }
    isNotMain && run(runChecks);
  };

  useEffect(() => {
    if (source === SPV_CONTRACTOR_MIRROR.CTR_MAIN) {
      //dispatch an event to set the matching contactlist if any;
      fxDispatch(setCurrentMirrorContactSelection(formdata.ClientId));
      //also set the buttons to disabled
      setResetButtonOptions({ ...resetButtonOptions, disabled: true });
      setDataSet(formdata);
    } else {
      setDataSet(formdata);
      guardianRef.current = structuredClone(formdata);
    }
  }, [sumdata]);

  useEffect(() => {
    isNotMain && run(runChecks);
  }, [dataSet]);

  return (
    <form id={"spv-client-form"} autoComplete="off">
      <Form
        ref={frmConRef}
        id="contractor-form"
        style={{ autofill: "off" }}
        formData={dataSet}
        className={`dx-swatch-catetory-chooser-compact ${
          source === SPV_CONTRACTOR_MIRROR.CTR_MAIN ? "" : "with-temp-data"
        }`}
        readOnly={source !== SPV_CONTRACTOR_MIRROR.CTR_MAIN}
        showColonAfterLabel={true}
        showValidationSummary={true}
        labelLocation="top"
        alignItemLabels={true}
        labelMode="static"
        onFieldDataChanged={(e) => {
          if (source !== SPV_CONTRACTOR_MIRROR.CTR_MAIN) {
            return;
          }
          const data = e.component.option("formData");
          if (sum(data) === sum(guardianRef.current)) {
            return;
          }
          const sdata = structuredClone(data);
          if (e.dataField === "CountryCode") {
            sdata.CountyCode = "";
            sdata.City = "";
            sdata.Address = "";
            sdata.PostalCode = "";
          }
          if (e.dataField === "CountyCode") {
            const subdiv = countrySubdivisions.find(
              (item) => item.COD_SUBDIV === e.value
            )?.DEN_SUBDIV;
            sdata.County = subdiv;
            sdata.City = "";
          }
          // if (e.dataField === 'City') {}
          const newDataSet = { ...dataSet, ...sdata };
          setDataSet(newDataSet);
          guardianRef.current = structuredClone(newDataSet);
          frmConRef.current?.instance().validate();
        }}
        onOptionChanged={onOptionChanged}
      >
        <SimpleItem
          render={() => (
            <HeaderComponent
              frmConRef={frmConRef}
              source={source}
              copyAll={copyAll}
              runRefresh={runRefresh}
              buttonState={buttons}
              outOfScopeOfTVA={outOfScopeOfTVA}
              bttnOptions={resetButtonOptions}
              caller={caller}
            />
          )}
        />
        <GroupItem caption="Identificare" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="UsingRoEfactura"
            editorType="dxSwitch"
            editorOptions={{
              label: { text: "eFactura" },
              width: 100,
              switchedOnText: "✓ cu eFactura",
              switchedOffText: "⊘ fara eFactura",
            }}
            label={{ text: "eFactura" }}
            //it does make sense to show in contacts not in spv
            visible={caller === contractorInEditMode}
          />

          {source === SPV_CONTRACTOR_MIRROR.CTR_MIRR_TMP &&
          caller === contractorInEditMode ? (
            //lookup
            <SimpleItem
              colSpan={2}
              editorType={"dxLookup"}
              editorOptions={dxClientLookupOptions}
              label={{ text: t("TempClients") }}
            ></SimpleItem>
          ) : source !== SPV_CONTRACTOR_MIRROR.CTR_MIRR_ARCHIVE ? (
            <SimpleItem
              colSpan={2}
              dataField={"ClientName"}
              editorType={"dxTextArea"}
              editorOptions={{
                // elementAttr: { class: "dx-state-invalid" },//??
                height: 60,
                /// if is supplier then BT-27
                /// if is customer  then BT-44
                // 'cac:AccountingCustomerParty|cac:Party|cac:PartyLegalEntity|cbc:RegistrationName|*'
                // 1..1
                placeholder: t("clientnameplaceholder"),
                buttons: getButtonDef("ClientName"),
              }}
              label={{ text: t("ClientName"), alignment: "center" }}
            >
              <RequiredRule message={t("ClientNameIsRequired")} />
            </SimpleItem>
          ) : (
            <SimpleItem
              colSpan={2}
              render={() => {
                const mamadata = mainConRef.current
                  ?.instance()
                  .option("formData").ClientId;
                const weMatch = mamadata === currentContactSelected;
                return (
                  <div>
                    <SelectBox
                      height={60}
                      style={{ flex: 1 }}
                      dataSource={contactList}
                      label="Denumire Client"
                      labelMode="static"
                      displayExpr={"text"}
                      valueExpr={"value"}
                      value={currentContactSelected}
                      searchEnabled={true}
                      searchExpr={["text"]}
                      searchMode={"contains"}
                      hint={
                        weMatch ? t("weMatch") : t("contact_selection_hint")
                      }
                      searchTimeout={200}
                      minSearchLength={2}
                      showDataBeforeSearch={false}
                      acceptCustomValue={false}
                      placeholder={t("start_typing")}
                      onValueChanged={(e) => {
                        fxDispatch(setCurrentMirrorContactSelection(e.value));
                      }}
                      showDropDownButton={true}
                    >
                      <SelectButton
                        name="linkButton"
                        location="before"
                        options={{
                          icon: "link",
                          elementAttr: {
                            class: `ctor-link-button  ${
                              weMatch ? "linked" : "unlinked"
                            }`,
                          },
                          stylingMode: "text",
                          width: 40,
                          height: 60,
                          onClick: (e) => {
                            const data = mainConRef.current
                              ?.instance()
                              .option("formData");
                            const newMainData = {
                              ...data,
                              ClientId: currentContactSelected,
                            };
                            mainConRef.current
                              ?.instance()
                              .option("formData", newMainData);
                            isNotMain && run(runChecks);
                          },
                        }}
                      ></SelectButton>
                      <SelectButton
                        name="dropDown"
                        location="after"
                        options={{
                          icon: "search",
                          stylingMode: "text",
                          width: 40,
                          height: 62,
                        }}
                      ></SelectButton>
                    </SelectBox>
                  </div>
                );
              }}
            ></SimpleItem>
          )}

          <SimpleItem
            dataField="TaxCode"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-48
              // 'cac:AccountingCustomerParty|cac:Party|cac:PartyTaxScheme|cbc:CompanyID|*'
              // 1..1
              placeholder: t("TaxCodePlaceholder"),
              buttons: getButtonDef("TaxCode"),
            }}
            label={{ text: t("TaxCode") }}
          >
            <RequiredRule message={t("TaxCodeIsRequired")} />
            <CustomRule
              message={t("no_whitespace")}
              validationCallback={(e) => {
                return !/\s/.test(`${e.value}`);
              }}
            />
          </SimpleItem>
          <SimpleItem
            dataField="RegistrationNumber"
            editorType={"dxTextBox"}
            editorOptions={{
              inputAttr: {
                type: "text",
                id: "RegistrationNumber",
                autoComplete: "off",
                autofill: "off",
              },
              height: 40,
              // customer: BT-47
              // supplier: BT-30
              // 'cac:AccountingCustomerParty|cac:Party|cac:PartyLegalEntity|cbc:CompanyID|*'
              // 0..1
              placeholder: t("RegistrationNumberPlaceholder"),
              buttons: getButtonDef("RegistrationNumber"),
            }}
            label={{ text: t("RegistrationNumber") }}
          />

          <SimpleItem
            colSpan={2}
            dataField="Iban"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: N/A (?)
              // supplier: BT-84
              // cac:PaymentMeans cac:PayeeFinancialAccount cbc:ID
              // 1..1
              placeholder: t("Ibanplacheholder"),
              buttons: getButtonDef("Iban"),
            }}
            label={{ text: t("Iban") }}
          >
            {/* <RequiredRule message={t('IbanIsRequired')} /> */}
          </SimpleItem>
          <SimpleItem
            colSpan={2}
            dataField="BankName"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: na
              // supplier: BT-85 <<
              // cbc:CompanyID
              // cac:PaymentMeans cac:PayeeFinancialAccount cbc:Name
              placeholder: t("BankNameplaceholder"),
              buttons: getButtonDef("BankName"),
            }}
            label={{ text: t("BankName") }}
          />
        </GroupItem>
        <GroupItem caption="Contact" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="Attention"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-
              // supplier: BT-56
              // 'cac:AccountingCustomerParty|cac:Party|cac:Contact|cbc:Name|*'
              // 0..1
              placeholder: t("Attentionplaceholder"),
              buttons: getButtonDef("Attention"),
            }}
            label={{ text: t("Attention") }}
          />

          <SimpleItem
            dataField="Phone"
            editorType={"dxTextBox"}
            editorOptions={{
              style: { autofill: "false" },
              height: 40,
              // customer: BT-57
              //cac:AccountingCustomerParty|cac:Party|cac:Contact|cbc:Telephone|*
              // supplier: BT-42
              // cac:AccountingSupplierParty cac:Party cac:Contact cbc:Telephone
              // 0..1
              placeholder: t("Phone"),
              buttons: getButtonDef("Phone"),
            }}
            label={{ text: t("Phonelabel") }}
          />
          <SimpleItem
            dataField="Fax"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-
              // supplier: BT-
              // cbc:CompanyID
              placeholder: "",
            }}
            label={{ text: t("Fax") }}
            visible={true}
          />
          <SimpleItem
            dataField="Email"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-58
              // supplier: BT-
              // cac:AccountingCustomerParty cac:Party cac:Contact cbc:ElectronicMail
              // 0..1
              placeholder: t("EmailPlaceholder"),
              buttons: getButtonDef("Email"),
            }}
            label={{ text: t("Email") }}
          >
            <EmailRule message={t("EmailInvalid")} />
          </SimpleItem>
        </GroupItem>
        <GroupItem colCount={2} caption="Localizare">
          {source === SPV_CONTRACTOR_MIRROR.CTR_MAIN ? (
            <SimpleItem
              dataField="CountryCode"
              editorType="dxSelectBox"
              editorOptions={{
                inputAttr: { autofill: "off", autoComplete: "off" },
                height: 40,
                dataSource: coutryList,
                displayExpr: "DEN_NATIONAL",
                valueExpr: "COD_NATIONAL",
                minSearchLength: 0,
                searchTimeout: 500,
                searchEnabled: true,
                searchMode: "startswith",
                searchExpr: ["DEN_NATIONAL", "COD_NATIONAL"],
                defaultValue: "RO",
                showDataBeforeSearch: true,
                acceptCustomValue: false,
              }}
              label={{ text: t("Country") }}
            >
              <RequiredRule message={t("Requiredfield")} />
            </SimpleItem>
          ) : (
            <SimpleItem
              dataField="CountryCode"
              editorType={"dxTextBox"}
              editorOptions={{
                height: 40,
                placeholder: t("CountryCodePlaceholder"),
                buttons: getButtonDef("CountryCode"),
              }}
              label={{ text: t("CountryCode") }}
            />
          )}
          {source === SPV_CONTRACTOR_MIRROR.CTR_MAIN ? (
            <SimpleItem
              dataField="CountyCode"
              editorType="dxSelectBox"
              editorOptions={{
                height: 40,
                dataSource: countrySubdivisions,
                displayExpr: "DEN_SUBDIV",
                valueExpr: "COD_SUBDIV",
                minSearchLength: 1,
                searchTimeout: 500,
                searchEnabled: true,
                searchMode: "startswith",
                searchExpr: ["DEN_SUBDIV"],
                defaultValue: "RO-B",
                showDataBeforeSearch: true,
                acceptCustomValue: false,
              }}
              label={{ text: t("County") }}
            >
              <RequiredRule message={t("Requiredfield")} />
            </SimpleItem>
          ) : (
            <SimpleItem
              dataField="CountyCode"
              editorType={"dxTextBox"}
              editorOptions={{
                height: 40,
                placeholder: t("CountyCodePlaceholder"),
                buttons: getButtonDef("CountyCode"),
              }}
              label={{ text: t("CountyCode") }}
            />
          )}

          <SimpleItem
            dataField="CountryCode"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-55
              // supplier: BT-xx
              // cac:AccountingCustomerParty cac:Party cac:PostalAddress cac:Country cbc:IdentificationCode
              placeholder: t("CountryCodePlaceholder"),
            }}
            label={{ text: t("CountryCode") }}
          >
            <RequiredRule message={t("Requiredfield")} />
          </SimpleItem>
          <SimpleItem
            dataField="CountyCode"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-54
              // supplier: BT-xx
              // cac:AccountingCustomerParty cac:Party cac:PostalAddress cbc:CountrySubentity
              placeholder: t("CountyCodePlaceholder"),
            }}
            label={{ text: t("CountyCode") }}
          >
            <RequiredRule message={t("Requiredfield")} />
          </SimpleItem>

          <SimpleItem
            colSpan={2}
            dataField="County"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              readOnly: true,
              // customer: BT-54
              // supplier: BT-37
              // 'cac:AccountingCustomerParty|cac:Party|cac:PostalAddress|cbc:CountrySubentity|*'
              // 0..1
              placeholder: t("countyplaceholder"),
              buttons: getButtonDef("County"),
            }}
            label={{ text: t("County") }}
            visible={false}
          />

          <SimpleItem
            dataField="City"
            colSpan={2}
            editorType={"dxSelectBox"}
            editorOptions={{
              height: 40,
              dataSource: cityOrDistrict,
              acceptCustomValue:
                frmConRef.current?.instance().option("formData").CountyCode !==
                "RO-B",
              displayExpr: "DEN_SUBDIV",
              valueExpr: "DEN_SUBDIV",
              onCustomItemCreating: customCityItemCreating,

              // customer: BT-52
              // supplier: BT-xx
              // cac:AccountingCustomerParty cac:Party cac:PostalAddress cbc:CityName
              // 0..1
              placeholder: t("CityPlaceholder"),
              buttons: getButtonDef("City"),
            }}
            label={{ text: t("City_sector") }}
          >
            <RequiredRule message={t("Requiredfield")} />
          </SimpleItem>

          <SimpleItem
            colSpan={2}
            dataField="Address"
            editorType={"dxTextArea"}
            editorOptions={{
              height: 60,
              // customer: BT-50
              // supplier: BT-35
              //'cac:AccountingCustomerParty|cac:Party|cac:PostalAddress|cbc:StreetName|*'
              // 0..1
              placeholder: t("addressplaceholder"),
              buttons: getButtonDef("Address"),
            }}
            label={{ text: t("Address") }}
          >
            <RequiredRule message={t("Requiredfield")} />
          </SimpleItem>
          <SimpleItem
            colSpan={2}
            dataField="__address__continuation"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-50
              // supplier: BT-35
              // cbc:StreetName
              // legacy field:
              // this field does not exist in the xml it will be concatenated with the address, using '||' as separator
              placeholder: t("addressplaceholder2"),
              buttons: getButtonDef("__address__continuation"),
            }}
            label={{ text: t("Address2") }}
          />

          <SimpleItem
            colSpan={2}
            dataField="AdditionalAddress"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-
              // supplier: BT-
              //cac:AccountingCustomerParty cac:Party cac:PostalAddress cac:AddressLine cbc:Line
              placeholder: t("AdditionalAddress"),
              buttons: getButtonDef("AdditionalAddress"),
            }}
            label={{ text: t("AdditionalAddressLabel") }}
            // visible={false}
          />

          <SimpleItem
            dataField="PostalCode"
            editorType={"dxTextBox"}
            editorOptions={{
              height: 40,
              // customer: BT-53
              // supplier: BT-xx
              //cac:AccountingCustomerParty cac:Party cac:PostalAddress cbc:PostalZone
              // 0..1
              placeholder: t("PostalCodePlaceholder"),
              buttons: getButtonDef("PostalCode"),
            }}
            label={{ text: t("PostalCode") }}
          />
        </GroupItem>
        <GroupItem caption="Alte informatii"></GroupItem>
        <SimpleItem
          dataField="BuyerReference"
          editorType={"dxTextBox"}
          editorOptions={{
            height: 40,
            // BT-10
            // cbc:BuyerReference
            placeholder: t("BuyerReferencePlaceholder"),
            buttons: getButtonDef("BuyerReference"),
          }}
          label={{ text: t("BuyerReference") }}
        />
      </Form>
    </form>
  );
};
